/**
 * @fileoverview Wrapper composable for common form validation logic including VeeValidate
 *
 * Some findings on VeeValidate:
 *
 * - The injected this.fields and this.errors become defined in the created hook
 *    - this.fields remains an empty object until mounted tick #1
 *    - this.errors remains an empty ErrorBag until validation completes and returns that errors exist
 * - Field flags are not accurate until mounted tick #2
 *
 * For more info: {@link https://fusion.mastercard.int/confluence/x/yVdbGg}
 */

import { computed, getCurrentInstance, onMounted, ref } from '@vue/composition-api'

export default function useFormValidation() {
  const { proxy } = getCurrentInstance()

  /**
   * LIFECYCLE
   */

  onMounted(async () => {
    await proxy.$nextTick()
    hasScannedFields.value = true
  })

  /**
   * REACTIVE
   */

  /**
   * Whether or not it has been at least one tick after mounting,
   * which is when VeeValidate scans for fields to validate in the DOM.
   */
  const hasScannedFields = ref(false)

  /**
   * Determine if VeeValidate has not validated yet or if there are validation errors
   * This solves the bug where the submit button flickers from enabled to disabled
   *
   * @returns {boolean}
   */
  const hasValidationErrors = computed(() => {
    const hasFields = proxy.fields && Object.keys(proxy.fields).length > 0

    /**
     * If it has passed the point where VeeValidate would scan for fields and there are no fields,
     * then there is nothing to validate so return false.
     *
     * This is useful in scenarios where there is prefilled data to review with an optional toggle to show input fields.
     * VeeValidate wouldn't find the fields initially so the submit button in those scenarios should be enabled.
     */
    if (hasScannedFields.value && !hasFields) {
      return false
    }

    const hasValidated = Object.values(proxy.fields).some(({ validated }) => validated)
    return !hasValidated || proxy.errors.any()
  })

  return { hasValidationErrors }
}
